import React, { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';

import {
    Box,
    Chip,
    CircularProgress,
    Container,
    Divider,
    IconButton,
    Stack,
    Typography,
} from '@mui/material';

import { blueGrey } from '@mui/material/colors';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';

const CATEGORY_LABELS = {
    update:      '업데이트',
    maintenance: '점검',
    security:    '보안',
    event:       '이벤트',
    general:     '일반',
};
const CATEGORY_COLORS = {
    update:      'primary',
    maintenance: 'warning',
    security:    'error',
    event:       'success',
    general:     'default',
};

function NoticeContentPage() {
    const navigate = useNavigate();
    const { id } = useParams();

    const [notice, setNotice]   = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError]     = useState(null);

    useEffect(() => {
        if (!id) return;
        setLoading(true);
        fetch(`/api/announcements/${id}`)
            .then(r => {
                if (!r.ok) throw new Error('not found');
                return r.json();
            })
            .then(data => setNotice(data))
            .catch(() => setError('공지사항을 불러오지 못했습니다.'))
            .finally(() => setLoading(false));
    }, [id]);

    const formatDate = (iso) => {
        if (!iso) return '';
        return iso.slice(0, 10).replace(/-/g, '. ') + '.';
    };

    return (
        <>
            {/* 상단 바 */}
            <Box sx={{ position: 'relative', width: '100%' }}>
                <Container sx={{ position: 'relative', width: '100%', py: '18px' }}>
                    <Stack direction="row" spacing={2} alignItems="center">
                        <IconButton onClick={() => navigate(-1)}>
                            <ArrowBackIcon />
                        </IconButton>
                        <Typography sx={{ fontFamily: 'NotoSansKR', fontWeight: 900, fontSize: '20px', color: blueGrey[800] }}>
                            공지사항
                        </Typography>
                    </Stack>
                </Container>
            </Box>

            <Container>
                <Box sx={{ my: 2, padding: '24px 0px 108px' }}>
                    {loading ? (
                        <Box sx={{ textAlign: 'center', py: 8 }}>
                            <CircularProgress />
                        </Box>
                    ) : error ? (
                        <Box sx={{ textAlign: 'center', py: 8 }}>
                            <Typography sx={{ fontFamily: 'NotoSansKR', color: blueGrey[400], fontSize: 15 }}>
                                {error}
                            </Typography>
                        </Box>
                    ) : notice ? (
                        <>
                            {/* 카테고리 + 제목 + 날짜 */}
                            <Stack direction="row" spacing={1} sx={{ mb: 1.5 }} alignItems="center">
                                <Chip
                                    label={CATEGORY_LABELS[notice.category] || notice.category}
                                    size="small"
                                    color={CATEGORY_COLORS[notice.category] || 'default'}
                                    variant="outlined"
                                    sx={{ fontFamily: 'NotoSansKR', fontSize: 11 }}
                                />
                            </Stack>

                            <Typography sx={{
                                fontFamily: 'NotoSansKR',
                                fontWeight: 900,
                                fontSize: '32px',
                                color: blueGrey[800],
                                mb: '8px',
                                lineHeight: 1.3,
                            }}>
                                {notice.title}
                            </Typography>

                            <Typography sx={{
                                fontFamily: 'NotoSansKR',
                                fontSize: '14px',
                                color: blueGrey[400],
                                mb: '20px',
                            }}>
                                {formatDate(notice.created_at)}
                                {notice.view_count != null && (
                                    <Box component="span" sx={{ ml: 2 }}>조회 {notice.view_count}</Box>
                                )}
                            </Typography>

                            <Divider sx={{ mb: '32px' }} />

                            {/* 본문 */}
                            <Typography sx={{
                                fontFamily: 'NotoSansKR',
                                fontSize: '16px',
                                color: blueGrey[800],
                                lineHeight: 1.9,
                                whiteSpace: 'pre-wrap',
                                wordBreak: 'break-word',
                            }}>
                                {notice.content}
                            </Typography>
                        </>
                    ) : null}
                </Box>
            </Container>
        </>
    );
}

export default NoticeContentPage;
